import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  OnInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LoyaltyProgramFacade } from '@features/loyalty-program/facades/loyalty-program.facade';
import { getCombinedOrderTransactionStatus } from '@features/order-ui/utils/order.utils';
import { OrderFacade } from '@features/order/facades/order-facade/order.facade';
import { PollingFacade } from '@features/polling/store/polling.facade';
import { AnalyticsService } from '@rd/core/services/analytics/analytics.service';
import { PageTitleService } from '@rd/core/services/page-title/page-title.service';
import { orderItemFilter } from '@rd/helpers/order.helper';
import {
  Order,
  OrderItemStatus,
  OrderItemType,
  PointsTransferOrderItem,
  SuccessButtonStatus,
  TransactionStatus,
  VoucherOrderItem,
} from '@rd/types';
import { isNotNull } from '@rd/utils/isNotNull';
import { combineLatest } from 'rxjs';
import { filter, first, map, startWith, switchMap } from 'rxjs/operators';
import { TransferConnectV2SettingService } from '../../services/transfer-connect-v2-setting/transfer-connect-v2-setting.service';

@Component({
  selector: 'rd-tcv2-success',
  templateUrl: './success.component.html',
  styleUrls: ['./success.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TcV2SuccessComponent implements OnInit {
  @HostBinding('class.rd-tcv2-success') componentClass = true;

  TransactionStatus = TransactionStatus;

  successButtons = this.transferConnectV2SettingService.successButtons();

  orderId = this.activatedRoute.snapshot.paramMap.get('orderId') ?? '';

  orderPolling$ = this.pollingFacade.getPolling(this.orderId);

  order$ = this.orderFacade.order$;

  pointsTransferLoyaltyProgram$ = this.order$.pipe(
    filter(isNotNull),
    switchMap((order) => {
      const orderItem = this.getPointsTransferOrVoucherOrderItem(order);
      const loyaltyProgramId = orderItem!.attributes.data.loyaltyProgramId;

      return this.loyaltyProgramFacade.getLoyaltyProgram(loyaltyProgramId);
    }),
    // We must emit null here so that vm$ can have all the initial
    // value for all observables
    startWith(null),
  );

  transactionStatus$ = combineLatest([
    this.orderFacade.getOrderError$,
    this.orderFacade.getOrderLoading$,
    this.orderPolling$,
    this.order$,
    this.pointsTransferLoyaltyProgram$,
  ]).pipe(
    map(([error, loading, pollingData, order, loyaltyProgram]) => {
      const status = getCombinedOrderTransactionStatus({
        pollingData,
        order,
        loading,
        error,
      });

      if (status === TransactionStatus.SUCCESS) {
        // We should also check if loyal program existed
        // cause success detail need loyaltyProgram to be available
        return !!loyaltyProgram ? status : TransactionStatus.LOADING;
      }

      return status;
    }),
  );

  vm$ = combineLatest([
    this.order$,
    this.pointsTransferLoyaltyProgram$,
    this.transactionStatus$,
  ]).pipe(
    map(([order, pointsTransferLoyaltyProgram, transactionStatus]) => ({
      order,
      pointsTransferLoyaltyProgram,
      transactionStatus,
      successButtonsStatus: this.getSuccessButtonsStatus(
        order?.fulfillmentStatus,
      ),
    })),
  );

  constructor(
    private activatedRoute: ActivatedRoute,
    private analyticsService: AnalyticsService,
    private transferConnectV2SettingService: TransferConnectV2SettingService,
    private pageTitleService: PageTitleService,
    private orderFacade: OrderFacade,
    private pollingFacade: PollingFacade,
    private loyaltyProgramFacade: LoyaltyProgramFacade,
  ) {}

  ngOnInit(): void {
    this.pageTitleService.setPageTitle('transfer-connect-success');

    this.orderFacade.getOrder(this.orderId);

    this.sendAnalyticsData();
  }

  private sendAnalyticsData(): void {
    this.analyticsService.track({
      // @ts-ignore
      routeName: this.analyticsService.sitemap.transferConnect.success,
    });

    this.order$.pipe(first((order) => !!order)).subscribe((order) => {
      const orderItem = this.getPointsTransferOrVoucherOrderItem(order!);

      this.analyticsService.track({
        routeName:
          // @ts-ignore
          this.analyticsService.sitemap.transferConnect.successFullData,
        extra: {
          brand: orderItem?.attributes.data.productName,
          status:
            order?.fulfillmentStatus === OrderItemStatus.Processing
              ? OrderItemStatus.Pending
              : order?.fulfillmentStatus,
        },
      });
    });
  }

  private getPointsTransferOrVoucherOrderItem(
    order: Order,
  ): PointsTransferOrderItem | VoucherOrderItem | undefined {
    return order.orderItems.find(
      orderItemFilter<PointsTransferOrderItem>(
        OrderItemType.PointsTransferOrderItem,
        // [Hau] need to support voucher order item as well
        // as it is part of the same points transfer flow
        OrderItemType.VoucherOrderItem,
      ),
    );
  }

  private getSuccessButtonsStatus(
    fulfillmentStatus: OrderItemStatus | undefined,
  ): SuccessButtonStatus {
    switch (fulfillmentStatus) {
      case OrderItemStatus.Processing:
      case OrderItemStatus.Pending: {
        return 'pending';
      }
      case OrderItemStatus.Fulfilled: {
        return 'confirmed';
      }
      default: {
        return 'failed';
      }
    }
  }
}
